import { useEffect, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { get, type RepoList as RepoPage } from '../../services/api';
import { RepoList } from '../../components/RepoList';
import { ErrorBox, Loading, PageHeader, Pagination } from '../../components/Ui';
import { Icon } from '../../components/Icon';
import { useAsync, useTitle } from '../../hooks';

const SORTS = [
  ['updated', 'Recently updated'],
  ['created', 'Newest'],
  ['forks', 'Most forked'],
  ['name', 'Name'],
] as const;
export function Explore() {
  useTitle('Explore wikis');
  const [params, setParams] = useSearchParams();
  const search = params.get('q') ?? '';
  const [text, setText] = useState(search);
  const requestedSort = params.get('sort');
  const sort = SORTS.some(([value]) => value === requestedSort) ? requestedSort! : 'updated';
  const topic = params.get('topic') ?? '';
  const page = Math.min(10000, Math.max(1, Math.floor(Number(params.get('page')) || 1)));
  const topics = useAsync(() => get<{ items: { topic: string; repositoryCount: number }[] }>('/topics'), []);
  useEffect(() => setText(search), [search]);
  const result = useAsync(
    () =>
      get<RepoPage>(
        `/repositories?perPage=15&sort=${sort}&page=${page}&q=${encodeURIComponent(search)}${topic ? `&topic=${encodeURIComponent(topic)}` : ''}`,
      ),
    [sort, page, search, topic],
  );
  const update = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== 'page') next.delete('page');
    setParams(next);
  };
  const submit = (event: FormEvent) => {
    event.preventDefault();
    update('q', text.trim());
  };
  return (
    <div className="container stack">
      <PageHeader
        title="Explore wikis"
        description="Search public wiki names, descriptions, and owners. Find your next read."
      />
      <div className="explore-layout">
        <aside className="explore-topics" aria-label="Browse topics">
          <h2>Browse topics</h2>
          <button className="topic-button" aria-pressed={!topic} onClick={() => update('topic', '')}>
            All topics
          </button>
          {topics.data?.items.map((item) => (
            <button
              className="topic-button"
              key={item.topic}
              aria-pressed={topic === item.topic}
              onClick={() => update('topic', item.topic)}
            >
              <span>{item.topic}</span>
              <span className="topic-count">{item.repositoryCount}</span>
            </button>
          ))}
          {topics.loading && (
            <p className="hint" role="status">
              Loading topics…
            </p>
          )}
        </aside>
        <div className="stack">
          <form className="filter-bar explore-toolbar" role="search" onSubmit={submit}>
            <div className="field">
              <label htmlFor="explore-q">Search public wikis</label>
              <input
                id="explore-q"
                className="input"
                type="search"
                maxLength={100}
                placeholder="Name, description, or owner"
                value={text}
                onChange={(event) => setText(event.target.value)}
              />
            </div>
            <button className="btn primary">
              <Icon name="search" />
              Search
            </button>
          </form>
          <div className="filter-bar">
            <div className="field">
              <label htmlFor="explore-sort">Sort by</label>
              <select
                id="explore-sort"
                className="input"
                value={sort}
                onChange={(event) => update('sort', event.target.value)}
              >
                {SORTS.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <div className="field mobile-topic-filter">
              <label htmlFor="explore-topic">Topic</label>
              <select
                id="explore-topic"
                className="input"
                value={topic}
                onChange={(event) => update('topic', event.target.value)}
              >
                <option value="">All topics</option>
                {topic && !topics.data?.items.some((item) => item.topic === topic) && (
                  <option value={topic}>{topic}</option>
                )}
                {topics.data?.items.map((item) => (
                  <option key={item.topic} value={item.topic}>
                    {item.topic} ({item.repositoryCount})
                  </option>
                ))}
              </select>
            </div>
          </div>
          {topics.error && <ErrorBox error={topics.error} retry={topics.reload} />}
          {(search || topic || sort !== 'updated') && (
            <div className="row active-filters" aria-label="Active filters">
              {search && <span className="badge">Search: {search}</span>}
              {topic && <span className="badge">Topic: {topic}</span>}
              {sort !== 'updated' && (
                <span className="badge">Sort: {SORTS.find(([value]) => value === sort)?.[1]}</span>
              )}
              <button
                className="btn sm"
                onClick={() => {
                  setText('');
                  setParams({});
                }}
              >
                Clear filters
              </button>
            </div>
          )}
          {result.loading && !result.data ? (
            <Loading />
          ) : result.error ? (
            <ErrorBox error={result.error} retry={result.reload} />
          ) : (
            result.data && (
              <>
                <p className="muted results-summary" aria-live="polite">
                  <span>
                    {result.data.total} public {result.data.total === 1 ? 'wiki' : 'wikis'}
                    {search ? ` matching "${search}"` : ''}
                    {topic ? ` tagged ${topic}` : ''}
                  </span>
                </p>
                <div className="card">
                  <RepoList
                    repos={result.data.items}
                    empty={search || topic ? 'No wikis match these filters' : 'No public wikis yet'}
                  />
                </div>
                {(search || topic) && !result.data.total && (
                  <p className="hint">Try another name or topic, or clear your filters.</p>
                )}
                <Pagination
                  page={result.data.page}
                  perPage={result.data.perPage}
                  total={result.data.total}
                  onPage={(value) => update('page', String(value))}
                />
              </>
            )
          )}
        </div>
      </div>
    </div>
  );
}
